import { useState, useRef } from 'react'
import { X, Upload, Loader2 } from 'lucide-react'
import Button from '../Button'
import { api } from '../../services/api'
import { getBackendUrl } from '../../utils/backendUrl'

export default function VideoInsertDialog({ onInsert, onClose }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef(null)

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0]
    if (selectedFile) {
      if (!selectedFile.type.startsWith('video/')) {
        setError('Please select a video file')
        return
      }
      if (selectedFile.size > 52428800) {
        setError('Video file size must be less than 50MB')
        return
      }
      setFile(selectedFile)
      setError(null)
    }
  }

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a video')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await api.post('/media/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      const videoUrl = response.data.data.url
      const fullVideoUrl = videoUrl.startsWith('http')
        ? videoUrl
        : `${getBackendUrl()}${videoUrl}`

      onInsert(fullVideoUrl)
      onClose()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to upload video')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Insert Video</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={uploading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/mov,video/avi,video/webm"
              onChange={handleFileChange}
              className="hidden"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="secondary"
              className="w-full"
              disabled={uploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              Choose Video
            </Button>
          </div>

          {file && (
            <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
              <div className="font-medium">{file.name}</div>
              <div className="text-xs mt-1">
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          <div className="text-xs text-gray-500">
            Supported formats: MP4, MOV, AVI, WebM (max 50MB)
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <Button variant="secondary" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              'Insert'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}
